/**
 * As peças comuns dos gráficos que vieram do dashboard do v1 (2026-10-25).
 *
 * - **Largura medida.** O SVG desenha na largura REAL do container
 *   (`useChartWidth`), com 1 unidade = 1 px. O `LineChart` aprendeu o
 *   contrário a duras penas: num `viewBox` fixo que encolhe com o painel,
 *   letra em rem vira ~5 px. Aqui a letra fica do tamanho do token.
 * - **Polaridade.** A ordem é a do domínio (`polarityOptions()`), e a cor
 *   vem da `palette` dele — nunca do nome escrito à mão. Neutro e "sem
 *   classificação" só entram quando têm menção; no modo percentual o
 *   "sem classificação" sai do denominador (a regra da Visão Geral de
 *   2026-10-09: menção sem polaridade não é neutra).
 * - **Dica.** Uma dica HTML posicionada sobre o gráfico, aberta pelo mouse
 *   OU pelo foco do teclado em cada marca (a marca é focável e tem
 *   `aria-label` com o mesmo texto).
 * - **Download.** PNG e SVG do QUADRO inteiro, sem os controles e sem fundo
 *   (sem biblioteca nova) — ver a seção do download, abaixo.
 */

import { Download } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject
} from "react";

import { DASHBOARD_NULL_KEY } from "@/shared/api/api-client";
import { polarityOptions, type PolarityPalette } from "@/shared/types/polarity";

// ─── largura ──────────────────────────────────────────────────────────

/** A largura do elemento, acompanhando o redimensionamento. Sem
 *  `ResizeObserver` (jsdom dos testes) fica no `fallback`. */
export function useChartWidth<T extends HTMLElement>(fallback = 720): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measured = element.getBoundingClientRect().width;
    if (measured > 0) setWidth(measured);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.width ?? 0;
      if (next > 0) setWidth(current => (Math.abs(current - next) >= 1 ? next : current));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

// ─── polaridade ───────────────────────────────────────────────────────

export type SegmentTone = PolarityPalette | "unclassified";

export type PolaritySegment = { key: string; label: string; tone: SegmentTone; value: number };

/** As fatias de uma contagem por polaridade, na ordem do domínio. */
export function polaritySegments(
  byPolarity: Record<string, number> | undefined,
  { percent = false }: { percent?: boolean } = {}
): PolaritySegment[] {
  const counts = byPolarity ?? {};
  const segments: PolaritySegment[] = polarityOptions()
    .map(option => ({ key: option.name, label: option.label, tone: option.palette as SegmentTone, value: counts[option.name] ?? 0 }))
    .filter(segment => segment.tone !== "neutral" || segment.value > 0);
  const unclassified = counts[DASHBOARD_NULL_KEY] ?? 0;
  if (!percent && unclassified > 0) {
    segments.push({ key: DASHBOARD_NULL_KEY, label: "Sem classificação", tone: "unclassified", value: unclassified });
  }
  if (!percent) return segments;
  const classified = segments.reduce((sum, segment) => sum + segment.value, 0);
  return segments.map(segment => ({ ...segment, value: classified ? (segment.value / classified) * 100 : 0 }));
}

/** As polaridades que aparecem em pelo menos uma das contagens — a legenda
 *  do gráfico, na mesma ordem das fatias. */
export function polarityLegend(all: Array<Record<string, number> | undefined>, percent = false): PolaritySegment[] {
  const totals: Record<string, number> = {};
  for (const counts of all) {
    for (const [key, value] of Object.entries(counts ?? {})) totals[key] = (totals[key] ?? 0) + value;
  }
  return polaritySegments(totals, { percent: false }).filter(segment => segment.value > 0 && (!percent || segment.tone !== "unclassified"));
}

export function ChartLegend({ items }: { items: Array<{ key: string; label: string; tone: string }> }) {
  if (!items.length) return null;
  return (
    <ul className="chart-legend" aria-label="Legenda">
      {items.map(item => (
        <li key={item.key}><span className={`chart-legend-swatch chart-tone--${item.tone}`} aria-hidden="true"/>{item.label}</li>
      ))}
    </ul>
  );
}

// ─── cores categóricas ────────────────────────────────────────────────

/** Até cinco entidades ganham cor própria, na ORDEM em que a lista chega
 *  (a cor segue a entidade); a sexta em diante vira "outros" — a regra da
 *  paleta validada em `tokens.css`. */
export function categoricalTone(index: number): string {
  return index < 5 ? `cat-${index + 1}` : "cat-other";
}

// ─── dica ─────────────────────────────────────────────────────────────

export type TipContent = { title: string; lines?: Array<{ label: string; value: string; tone?: string }> };
type TipState = TipContent & { x: number; y: number };

/** A dica flutuante de um gráfico: `bind(content)` devolve os handlers de
 *  mouse e foco pra pôr numa marca; `tip` é o elemento a renderizar dentro
 *  do contêiner relativo (`.chart-frame`). */
export function useChartTip(frame: RefObject<HTMLElement | null>) {
  const [tip, setTip] = useState<TipState | null>(null);
  const place = useCallback((content: TipContent, clientX: number, clientY: number) => {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;
    setTip({ ...content, x: clientX - box.left, y: clientY - box.top });
  }, [frame]);
  const bind = useCallback((content: TipContent) => ({
    onMouseMove: (event: { clientX: number; clientY: number }) => place(content, event.clientX, event.clientY),
    onMouseLeave: () => setTip(null),
    onFocus: (event: { currentTarget: Element }) => {
      const rect = event.currentTarget.getBoundingClientRect();
      place(content, rect.left + rect.width / 2, rect.top);
    },
    onBlur: () => setTip(null)
  }), [place]);
  const width = frame.current?.getBoundingClientRect().width ?? 0;
  const element = tip ? (
    <div
      className={`chart-tip${tip.x > width - 200 ? " is-left" : ""}`}
      style={{ left: tip.x, top: tip.y }}
      role="presentation"
    >
      <strong>{tip.title}</strong>
      {tip.lines?.map(line => (
        <span key={line.label}>
          {line.tone && <i className={`chart-legend-swatch chart-tone--${line.tone}`} aria-hidden="true"/>}
          {line.label}<b>{line.value}</b>
        </span>
      ))}
    </div>
  ) : null;
  return { bind, tip: element };
}

// ─── download ─────────────────────────────────────────────────────────
//
// O download leva o QUADRO inteiro — título, subtítulo, gráfico, legenda,
// destaques, tabela —, sem os controles (seletores, 123/%, os próprios
// botões) e SEM FUNDO (pedido de 2026-10-25: a imagem vai pra apresentação,
// sobre o fundo dela). Sem biblioteca nova: o painel é clonado com o
// estilo COMPUTADO de cada nó escrito no próprio nó, posto dentro de um
// SVG (`foreignObject`) e, pro PNG, desenhado num canvas. A Inter vai
// embutida (só os subconjuntos latinos): uma imagem SVG não carrega fonte
// de fora, e sem isso o PNG sairia na fonte do sistema.

/** O que NÃO sai na imagem: controles e a dica flutuante. */
export const EXPORT_IGNORE =
  "[data-export-ignore], .chart-controls, .chart-download, .chart-toggle, .dashboard-panel-actions, .matrix-controls, .matrix-items, .chart-tip, select, .theme-notes-foot, .theme-avatar-editor";

const COPIED_PROPERTIES = [
  "display", "position", "top", "right", "bottom", "left", "z-index", "box-sizing",
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding-top", "padding-right", "padding-bottom", "padding-left",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
  "border-top-style", "border-right-style", "border-bottom-style", "border-left-style",
  "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
  "border-top-left-radius", "border-top-right-radius", "border-bottom-right-radius", "border-bottom-left-radius",
  "flex-direction", "flex-wrap", "flex-grow", "flex-shrink", "flex-basis", "align-items", "align-content",
  "align-self", "justify-content", "justify-items", "justify-self", "order",
  "grid-template-columns", "grid-template-rows", "grid-column-start", "grid-column-end",
  "grid-row-start", "grid-row-end", "grid-auto-flow", "row-gap", "column-gap",
  "color", "background-color", "background-image", "background-size", "background-position", "background-repeat",
  "font-family", "font-size", "font-weight", "font-style", "font-variant-numeric", "line-height", "letter-spacing",
  "text-align", "text-transform", "text-decoration-line", "text-overflow", "white-space", "word-break", "overflow-wrap",
  "vertical-align", "list-style-type", "overflow-x", "overflow-y", "opacity", "transform", "transform-origin",
  "visibility", "border-collapse", "border-spacing", "table-layout",
  "-webkit-line-clamp", "-webkit-box-orient",
  "fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-opacity",
  "stroke-linecap", "stroke-linejoin", "text-anchor", "dominant-baseline", "paint-order"
];

function inlineStyles(source: Element, target: Element) {
  const style = window.getComputedStyle(source);
  const declarations = COPIED_PROPERTIES.map(property => `${property}:${style.getPropertyValue(property)}`).join(";");
  target.setAttribute("style", declarations);
  // O clone leva o HTML, não o estado: o texto digitado numa caixa mora
  // na propriedade `value`, e sem isto sairia a caixa vazia.
  if (source instanceof HTMLTextAreaElement) target.textContent = source.value;
  if (source instanceof HTMLInputElement) target.setAttribute("value", source.value);
  // `tabindex`/`aria` não pesam na imagem; o que importa é a mesma árvore.
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let index = 0; index < sourceChildren.length; index += 1) {
    if (targetChildren[index]) inlineStyles(sourceChildren[index], targetChildren[index]);
  }
}

let embeddedFonts: Promise<string> | null = null;

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** As regras `@font-face` da Inter (latim) com o arquivo embutido. Lido
 *  uma vez por sessão; falha vira texto vazio (a imagem sai na fonte do
 *  sistema, mas sai). */
function fontFaces(): Promise<string> {
  embeddedFonts ??= (async () => {
    const rules: CSSFontFaceRule[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
      let list: CSSRuleList;
      try { list = sheet.cssRules; } catch { continue; }
      for (const rule of Array.from(list)) {
        if (rule instanceof CSSFontFaceRule && /inter/i.test(rule.style.getPropertyValue("font-family"))) {
          const range = rule.style.getPropertyValue("unicode-range");
          if (!range || /U\+0000|U\+0100/i.test(range)) rules.push(rule);
        }
      }
    }
    const faces = await Promise.all(rules.map(async rule => {
      const source = rule.style.getPropertyValue("src");
      const match = source.match(/url\(["']?([^"')]+)["']?\)/);
      if (!match) return "";
      const base = rule.parentStyleSheet?.href ?? document.baseURI;
      const response = await fetch(new URL(match[1], base).href);
      const data = await toDataUrl(await response.blob());
      return rule.cssText.replace(match[0], `url("${data}")`);
    }));
    return faces.join("\n");
  })().catch(() => "");
  return embeddedFonts;
}

/** O quadro como SVG autossuficiente. */
export async function panelSvg(root: HTMLElement): Promise<{ text: string; width: number; height: number }> {
  const width = Math.ceil(root.getBoundingClientRect().width);
  const clone = root.cloneNode(true) as HTMLElement;
  inlineStyles(root, clone);
  clone.querySelectorAll(EXPORT_IGNORE).forEach(node => node.remove());
  // Sem fundo: o quadro em si fica transparente (sem cor, borda, sombra
  // nem o vidro); o que está dentro dele mantém o que tem.
  Object.assign(clone.style, {
    backgroundColor: "transparent", backgroundImage: "none", borderColor: "transparent",
    boxShadow: "none", margin: "0", width: `${width}px`, height: "auto", minHeight: "0"
  });
  // A altura muda ao sair os controles: mede o clone fora da tela.
  const stage = document.createElement("div");
  Object.assign(stage.style, { position: "fixed", left: "-100000px", top: "0", width: `${width}px` });
  stage.appendChild(clone);
  document.body.appendChild(stage);
  const height = Math.max(1, Math.ceil(clone.getBoundingClientRect().height));
  stage.remove();
  const fonts = await fontFaces();
  const body = new XMLSerializer().serializeToString(clone);
  const text = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<foreignObject x="0" y="0" width="${width}" height="${height}">`,
    `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px">`,
    fonts ? `<style>${fonts}</style>` : "",
    body,
    "</div></foreignObject></svg>"
  ].join("");
  return { text, width, height };
}

function slug(name: string): string {
  return name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "grafico";
}

function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadSvg(root: HTMLElement, name: string): Promise<void> {
  const { text } = await panelSvg(root);
  save(new Blob([text], { type: "image/svg+xml;charset=utf-8" }), `${slug(name)}.svg`);
}

export async function downloadPng(root: HTMLElement, name: string, scale = 2): Promise<void> {
  const { text, width, height } = await panelSvg(root);
  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const context = canvas.getContext("2d");
      if (!context) { reject(new Error("canvas indisponível")); return; }
      // Sem preencher o fundo: o PNG sai transparente.
      context.scale(scale, scale);
      context.drawImage(image, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (blob) { save(blob, `${slug(name)}.png`); resolve(); }
        else reject(new Error("PNG não gerado"));
      }, "image/png");
    };
    image.onerror = () => reject(new Error("SVG não carregou"));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
  });
}

/** Os dois botões de download. Exportam o QUADRO em que estão (o
 *  `[data-export-root]` mais próximo — o cartão de cada matriz —, senão o
 *  painel). */
export function ChartDownload({ name }: { name: string }) {
  const [failed, setFailed] = useState(false);
  const own = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!failed) return;
    const timer = window.setTimeout(() => setFailed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [failed]);
  const run = (download: (root: HTMLElement, name: string) => Promise<void>) => {
    const root = own.current?.closest<HTMLElement>("[data-export-root], .dashboard-panel");
    if (root) download(root, name).catch(() => setFailed(true));
  };
  return (
    <span className="chart-download" role="group" aria-label={`Baixar ${name}`} ref={own}>
      <button type="button" className="chart-download-button" onClick={() => run(downloadPng)} title="Baixar PNG">
        <Download size={13} aria-hidden="true"/>PNG
      </button>
      <button type="button" className="chart-download-button" onClick={() => run(downloadSvg)} title="Baixar SVG">SVG</button>
      {failed && <span className="chart-download-error" role="status">Não deu pra gerar a imagem.</span>}
    </span>
  );
}

// ─── controles ────────────────────────────────────────────────────────

/** Interruptor de duas ou três opções (123 / % / CENA). */
export function ChartToggle<T extends string>({ value, options, onChange, label }: {
  value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void; label: string;
}) {
  return (
    <div className="chart-toggle" role="radiogroup" aria-label={label}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={value === option.value ? "active" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ChartControls({ children }: { children: ReactNode }) {
  return <div className="chart-controls">{children}</div>;
}
